import {TPeriodPickerValue} from '@edna/components';

import {EChannelType, EOperatorName, TItemId} from 'src/constants';

export enum ETransactionType {
  // WHATSAPP
  WHATSAPP_AMAU_PAYMENT = 'WHATSAPP_AMAU_PAYMENT',
  WHATSAPP_CONVERSATION_BILLED = 'WHATSAPP_CONVERSATION_BILLED',
  WHATSAPP_LICENSE_PAYMENT = 'WHATSAPP_LICENSE_PAYMENT',
  WHATSAPP_CONNECTION_FEE = 'WHATSAPP_CONNECTIONFEE',
  WHATSAPP_CORRECTION_BILLED = 'WHATSAPP_CORRECTION_BILLED',
  WHATSAPP_OUT_MESSAGE_BILLED = 'WHATSAPP_OUT_MESSAGE_BILLED',
  // VIBER
  VIBER_OUT_MESSAGE_BILLED = 'VIBER_OUT_MESSAGE_BILLED',
  VIBER_RECURRENT_PAYMENT = 'VIBER_RECURRENT_PAYMENT',
  VIBER_LICENSE_PAYMENT = 'VIBER_LICENSE_PAYMENT',
  VIBER_CORRECTION_BILLED = 'VIBER_CORRECTION_BILLED',
  // SMS
  SMS_OUT_MESSAGE_BILLED = 'SMS_OUT_MESSAGE_BILLED',
  SMS_RECURRENT_BILLED = 'SMS_RECURRENT_BILLED',
  SMS_TEMPLATE_BILLED = 'SMS_TEMPLATE_BILLED',
  SMS_LICENSE_PAYMENT = 'SMS_LICENSE_PAYMENT',
  // Other channels
  MAX_BOT_LICENSE_PAYMENT = 'MAX_BOT_LICENSE_PAYMENT',
  MAX_LICENSE_PAYMENT = 'MAX_LICENSE_PAYMENT',
  TG_LICENSE_PAYMENT = 'TG_LICENSE_PAYMENT',
  // MANUAL
  MANUAL_BALANCE_ADD_FUNDS = 'MANUAL_BALANCE_ADD_FUNDS',
  MANUAL_BALANCE_REMOVE_FUNDS = 'MANUAL_BALANCE_REMOVE_FUNDS',
  ALFA_PAYMENT = 'ALFA_PAYMENT',
  INVOICE = 'INVOICE',
  STRIPE_CHECKOUT_AUTO_REPLENISH = 'STRIPE_CHECKOUT_AUTO_REPLENISH',
  PAYKEEPER_PAYMENT = 'PAYKEEPER_PAYMENT',
  PAYKEEPER_FULL_REFUND = 'PAYKEEPER_FULL_REFUND',
  PAYKEEPER_PARTIAL_REFUND = 'PAYKEEPER_PARTIAL_REFUND',
  PAYKEEPER_RECURRENT_PAYMENT = 'PAYKEEPER_RECURRENT_PAYMENT',
}

export enum ETransactionTrafficType {
  SERVICE = 'SERVICE',
  // SMS
  MULTINAMING = 'MULTINAMING',
  AD = 'AD',
  INFO = 'INFO',
  MRKT = 'MRKT',
  TEMPLATE_ADV = 'TEMPLATE_ADV',
  ADVERTISEMENT = 'ADVERTISEMENT',
  AUTH = 'AUTH',
  // VIBER
  MARKETING = 'MARKETING',
}

export enum EPaymentType {
  INIT = 'INIT',
  RENEWAL = 'RENEWAL',
}

type TGenericTransaction<T extends ETransactionType, D = never> = {
  reasonCode: T;
  price: number;
  isDebit?: boolean;
} & D;

type TWhatsAppStatement = TGenericTransaction<
  ETransactionType.WHATSAPP_AMAU_PAYMENT,
  {
    subject: string;
    subjectId: number;
    licenseId: number;
    channelAttribute: string;
    isStorned?: boolean;
    mauTotal: number;
    count: number;
  }
>;

type TLicense = {
  licenseId?: number;
  periodStarted: string;
  periodEnded: string;
  isStorned?: boolean;
  paymentType: EPaymentType;
};

type TWhatsAppLicense = TGenericTransaction<
  ETransactionType.WHATSAPP_LICENSE_PAYMENT,
  TLicense & {
    subject: string;
    subjectId: number;
    channelAttribute: string;
    mauTotal: number;
  }
>;

type TWAConnectionFee = TGenericTransaction<
  ETransactionType.WHATSAPP_CONNECTION_FEE,
  {
    channelAttribute: string;
    mauCount: number;
    period: string;
    subjectId: number;
    isStorned?: boolean;
  }
>;

type TViberLicense = TGenericTransaction<ETransactionType.VIBER_LICENSE_PAYMENT, TLicense>;
type TSMSLicense = TGenericTransaction<ETransactionType.SMS_LICENSE_PAYMENT, TLicense>;
type TMaxBotLicense = TGenericTransaction<ETransactionType.MAX_BOT_LICENSE_PAYMENT, TLicense>;
type TMaxLicense = TGenericTransaction<ETransactionType.MAX_LICENSE_PAYMENT, TLicense>;
type TTelegramLicense = TGenericTransaction<ETransactionType.TG_LICENSE_PAYMENT, TLicense>;

type TOutMessageBilled = TGenericTransaction<
  | ETransactionType.VIBER_OUT_MESSAGE_BILLED
  | ETransactionType.SMS_OUT_MESSAGE_BILLED
  | ETransactionType.WHATSAPP_OUT_MESSAGE_BILLED
  | ETransactionType.WHATSAPP_CONVERSATION_BILLED,
  {
    subjectId: number;
    subject: string;
    channelAttribute: string;
    category: ETransactionTrafficType;
    count: number;
  }
>;

type TRecurrentPayment = {
  subject: string;
  subjectId?: number;
  channelAttribute?: string;
  periodStarted: string;
  periodEnded: string;
  isStorned?: boolean;
};

type TViberRecurrentPayment = TGenericTransaction<
  ETransactionType.VIBER_RECURRENT_PAYMENT,
  TRecurrentPayment
>;

type TSmsRecurrentPayment = TGenericTransaction<
  ETransactionType.SMS_RECURRENT_BILLED,
  TRecurrentPayment & {
    tariffId?: number;
    mobileOperator: EOperatorName;
  }
>;

type TSmsTemplatePayment = TGenericTransaction<
  ETransactionType.SMS_TEMPLATE_BILLED,
  TRecurrentPayment & {
    templateId?: number;
    templateName: string;
    mobileOperator: EOperatorName;
  }
>;

type TManualBalance = TGenericTransaction<
  ETransactionType.MANUAL_BALANCE_REMOVE_FUNDS | ETransactionType.MANUAL_BALANCE_ADD_FUNDS,
  {
    operationDescription: string;
    isStorned?: boolean;
  }
>;

type TInvoice = TGenericTransaction<
  ETransactionType.INVOICE | ETransactionType.STRIPE_CHECKOUT_AUTO_REPLENISH,
  {isStorned?: boolean}
>;

type TPayKeeperPayment = TGenericTransaction<
  | ETransactionType.PAYKEEPER_PAYMENT
  | ETransactionType.PAYKEEPER_FULL_REFUND
  | ETransactionType.PAYKEEPER_PARTIAL_REFUND
  | ETransactionType.PAYKEEPER_RECURRENT_PAYMENT,
  {
    tenantId: number;
    inn: string;
    email: string;
    paymentDate?: string;
  }
>;

type TCorrectionBilled = TGenericTransaction<
  ETransactionType.WHATSAPP_CORRECTION_BILLED | ETransactionType.VIBER_CORRECTION_BILLED,
  {
    purpose: string;
    isStorned?: boolean;
  }
>;

type TAlfaPayment = TGenericTransaction<
  ETransactionType.ALFA_PAYMENT,
  {paymentPurpose: string; isStorned?: boolean}
>;

export const channelByTransactionType: Partial<Record<ETransactionType, EChannelType>> = {
  [ETransactionType.WHATSAPP_AMAU_PAYMENT]: EChannelType.WHATSAPP,
  [ETransactionType.WHATSAPP_CONVERSATION_BILLED]: EChannelType.WHATSAPP,
  [ETransactionType.WHATSAPP_LICENSE_PAYMENT]: EChannelType.WHATSAPP,
  [ETransactionType.WHATSAPP_CONNECTION_FEE]: EChannelType.WHATSAPP,
  [ETransactionType.WHATSAPP_CORRECTION_BILLED]: EChannelType.WHATSAPP,

  [ETransactionType.VIBER_LICENSE_PAYMENT]: EChannelType.VIBER,
  [ETransactionType.VIBER_OUT_MESSAGE_BILLED]: EChannelType.VIBER,
  [ETransactionType.VIBER_RECURRENT_PAYMENT]: EChannelType.VIBER,
  [ETransactionType.VIBER_CORRECTION_BILLED]: EChannelType.VIBER,

  [ETransactionType.SMS_LICENSE_PAYMENT]: EChannelType.SMS,
  [ETransactionType.SMS_OUT_MESSAGE_BILLED]: EChannelType.SMS,
  [ETransactionType.SMS_RECURRENT_BILLED]: EChannelType.SMS,
  [ETransactionType.SMS_TEMPLATE_BILLED]: EChannelType.SMS,

  [ETransactionType.MAX_BOT_LICENSE_PAYMENT]: EChannelType.MAX_BOT,
  [ETransactionType.MAX_LICENSE_PAYMENT]: EChannelType.MAX,

  [ETransactionType.TG_LICENSE_PAYMENT]: EChannelType.TELEGRAM,
};

export const TRANSACTION_MANUAL_TYPES = [
  ETransactionType.MANUAL_BALANCE_ADD_FUNDS,
  ETransactionType.MANUAL_BALANCE_REMOVE_FUNDS,
  ETransactionType.ALFA_PAYMENT,
  ETransactionType.INVOICE,
  ETransactionType.STRIPE_CHECKOUT_AUTO_REPLENISH,
  ETransactionType.PAYKEEPER_PAYMENT,
  ETransactionType.PAYKEEPER_FULL_REFUND,
  ETransactionType.PAYKEEPER_PARTIAL_REFUND,
  ETransactionType.PAYKEEPER_RECURRENT_PAYMENT,
];

export enum ETransactionAmountType {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
  ALL = 'ALL',
}

export type TTransaction =
  | TWhatsAppStatement
  | TWhatsAppLicense
  | TWAConnectionFee
  | TSMSLicense
  | TSmsRecurrentPayment
  | TSmsTemplatePayment
  | TMaxBotLicense
  | TMaxLicense
  | TTelegramLicense
  | TViberLicense
  | TViberRecurrentPayment
  | TOutMessageBilled
  | TCorrectionBilled
  | TManualBalance
  | TAlfaPayment
  | TPayKeeperPayment
  | TInvoice;

export type TTransactionDate = {
  date: string;
  hasStornedTransactions?: boolean;
  hasUpdatedTransactions?: boolean;
};

export type TTransactions = {
  amountCredited?: number;
  amountDebited?: number;
  date: string;
  data: TTransaction[];
};

export type TFilters = {
  period: TPeriodPickerValue;
  tenantId?: TItemId;
  entryType: ETransactionAmountType;
};

export type TTransactionFilters = {
  entryType: ETransactionAmountType;
  tenantId?: TItemId;
  date: string;
};
